import React, { useState, ChangeEvent, FormEvent } from 'react';
import {
  Box,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  InputAdornment,
  OutlinedInput,
  FormHelperText,
} from '@mui/material';
import { ICoupon } from '../../../utils/entities/interface';
import { ImageUpload } from '../../atoms/UploadImage'; // Ensure this path is correct
import { addEditCoupon } from '../../../services/coupen';
import useSnackbar from '../../../hooks/useSnackbar';
import { DateField } from '../../../pages/Booking/Details';

const emptyCoupon: Omit<ICoupon, '_id' | 'createdAt' | 'updatedAt'> = {
  couponCode: '',
  image: '',
  name: '',
  discount: 0,
  minValue: 0,
  expiryDate: new Date(),
  description: [],
  isActive: true,
};

const CreateCouponForm = ({
  handleDrawerClose,
  onSaved,
  editingCoupon,
}: {
  handleDrawerClose?: () => void;
  onSaved?: () => void;
  editingCoupon?: ICoupon | null;
}) => {
  const { showSnackbar, SnackbarComponent } = useSnackbar();
  const isEditMode = Boolean(editingCoupon);
  const [coupon, setCoupon] = useState<
    Omit<ICoupon, '_id' | 'createdAt' | 'updatedAt'>
  >(
    editingCoupon
      ? { ...editingCoupon, expiryDate: new Date(editingCoupon.expiryDate) }
      : emptyCoupon,
  );

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // ✅ Coupon code validation
    if (name === 'couponCode') {
      const regex = /^[a-zA-Z0-9]*$/;
      if (!regex.test(value)) return;
    }

    let updatedValue: any = value;

    // ✅ Handle number fields (remove leading zeros)
    if (name === 'discount' || name === 'minValue') {
      updatedValue = value.replace(/^0+(?=\d)/, ''); // "012" → "12"
    }

    setCoupon((prev) => ({
      ...prev,
      [name]: name === 'description' ? value.split(',') : updatedValue,
      expiryDate: name === 'expiryDate' ? new Date(value) : prev.expiryDate,
    }));
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const expiryDate = new Date(coupon.expiryDate);
      if (isNaN(expiryDate.getTime())) {
        throw new Error('Invalid expiry date');
      }
      // Add your validation or any other logic here
      await addEditCoupon({
        ...coupon,
        expiryDate: expiryDate, // Save it in ISO string format
        _id: editingCoupon?._id || '',
      });
      onSaved?.();
      resetForm();
      showSnackbar(
        isEditMode ? 'Coupon updated successfully' : 'Coupon saved successfully',
        'success',
      );
      setTimeout(() => {
        handleDrawerClose?.();
      }, 1000);
    } catch (error: any) {
      showSnackbar(`Error occurred: ${error.message}`, 'error');
    }
  };
  const resetForm = () => {
    setCoupon(
      editingCoupon
        ? { ...editingCoupon, expiryDate: new Date(editingCoupon.expiryDate) }
        : emptyCoupon,
    );
  };

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <Box style={{ flexGrow: 1 }}>
        {SnackbarComponent}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Coupon Code"
              name="couponCode"
              value={coupon.couponCode}
              onChange={handleChange}
              fullWidth
              required
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Name"
              name="name"
              value={coupon.name}
              onChange={handleChange}
              fullWidth
              required
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined">
              <InputLabel htmlFor="discount">Discount</InputLabel>
              <OutlinedInput
                id="discount"
                name="discount"
                type="number"
                value={coupon.discount}
                onChange={handleChange}
                onWheel={(e) => (e.target as HTMLElement).blur()}
                onKeyDown={(e) => {
                  if (['-', '+', 'e', 'E', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                startAdornment={
                  <InputAdornment position="start">%</InputAdornment>
                }
                label="Discount"
                sx={{
                  // 🔥 Remove arrows
                  '& input[type=number]': {
                    MozAppearance: 'textfield', // Firefox
                  },
                  '& input[type=number]::-webkit-outer-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                  '& input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                }}
              />
              <FormHelperText>Enter discount percentage</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined">
              <InputLabel htmlFor="minValue">Minimum Value</InputLabel>
              <OutlinedInput
                id="minValue"
                name="minValue"
                type="number"
                value={coupon.minValue}
                onChange={handleChange}
                onWheel={(e) => (e.target as HTMLElement).blur()}
                onKeyDown={(e) => {
                  if (['-', '+', 'e', 'E', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                startAdornment={
                  <InputAdornment position="start">£</InputAdornment>
                }
                label="Minimum Value"
                sx={{
                  // 🔥 Remove arrows
                  '& input[type=number]': {
                    MozAppearance: 'textfield', // Firefox
                  },
                  '& input[type=number]::-webkit-outer-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                  '& input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                }}
              />
              <FormHelperText>Enter minimum order value</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <ImageUpload
              imageUrl={coupon.image}
              onChange={(url: string) =>
                setCoupon((prev) => ({ ...prev, image: url }))
              }
              onRemove={() => setCoupon((prev) => ({ ...prev, image: '' }))}
              uploadLabel="Upload Image"
              fileId="coupon-image-upload"
              type="banner"
              folderType={7}
              disabled={false}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Description"
              name="description"
              value={coupon.description}
              onChange={handleChange}
              fullWidth
              multiline
              rows={5}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            {/* <TextField
              label="Expiry Date"
              name="expiryDate"
              type="date"
              value={coupon.expiryDate.toISOString().split('T')[0]} // Ensure it's formatted correctly
              onChange={handleChange}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              variant="outlined"
            /> */}

            <DateField
              name="expiryDate"
              label="Expiry Date"
              type="date"
              value={coupon.expiryDate.toISOString().split('T')[0] || ''}
              onChange={handleChange}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
              variant="outlined"
            />
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          sx={{ flexGrow: 1, mr: 1 }}
        >
          {isEditMode ? 'Update Coupon' : 'Save Coupon'}
        </Button>
        <Button
          type="button"
          variant="outlined"
          color="secondary"
          onClick={resetForm}
          sx={{ flexGrow: 1, ml: 1 }}
        >
          Reset
        </Button>
      </Box>
    </Box>
  );
};

export default CreateCouponForm;
